import Vue from 'vue';
import Vuex from 'vuex';
import { UUIDUtil } from './core/services/uuid.util';
import { LoginData } from './model/auth.model';
import { Family, newFamily } from './model/family.model';
import { FamilyMember, Member } from './model/member.model';
import { Notification, NotificationType } from './model/notification.model';
import { Reporting } from './model/reporting.model';
import authService from './services/auth.service';
import familyService from './services/family.service';
import uploadService from './services/upload.service';
import { modalStore } from './stores/modal.store';
import { FieldSelectItem } from '@/model/field-select.model';

Vue.use(Vuex);

export enum StoreMutations {
  SetLoggedIn = 'setLoggedIn',
  Notify = 'notify',
  Dismiss = 'dismiss',
  SetFamily = 'setFamily',
  NewFamily = 'newFamily',
  SetFamilies = 'setFamilies',
  SetMembers = 'setMembers',
  SetReporting = 'setReporting',
}

export enum StoreActions {
  Login = 'login',
  Logout = 'logout',
  Auth = 'auth',
  Upload = 'upload',
  Notify = 'notify',
  Dismiss = 'dismiss',
  GetFamily = 'getFamily',
  GetFamilies = 'getFamilies',
  GetMembers = 'getMembers',
  SaveFamily = 'saveFamily',
  DeleteFamily = 'deleteFamily',
  GetReporting = 'getReporting',
  SaveRegistration = 'saveRegistration',
  ApproveSignup = 'approveSignup',
  PruneMembers = 'pruneMembers',
}

export interface DoorbraakStore {
  loggedIn: boolean;
  family: Family | null;
  families: Family[];
  members: Member[];
  reporting: Reporting[];
  notifications: Notification[];
}

export default new Vuex.Store<DoorbraakStore>({
  modules: {
    ms: modalStore,
  },
  state: {
    loggedIn: false,
    notifications: [],
    family: null,
    families: [],
    members: [],
    reporting: [],
  },
  mutations: {
    [StoreMutations.SetLoggedIn](state: DoorbraakStore, loggedIn: boolean) {
      state.loggedIn = loggedIn;
    },
    [StoreMutations.Notify](state, notification: Notification) {
      state.notifications = [...state.notifications, notification];
    },
    [StoreMutations.Dismiss](state, uuid) {
      state.notifications = [
        ...state.notifications.filter((n) => n.uuid !== uuid),
      ];
    },
    [StoreMutations.SetFamily](state, family: Family) {
      state.family = family;
    },
    [StoreMutations.NewFamily](state) {
      state.family = { ...newFamily };
    },
    [StoreMutations.SetFamilies](state, families: Family[]) {
      state.families = families;
    },
    [StoreMutations.SetMembers](state, members: Member[]) {
      state.members = members;
    },
    [StoreMutations.SetReporting](state, reporting: Reporting[]) {
      state.reporting = reporting;
    },
  },
  actions: {
    async [StoreActions.Login]({ commit, dispatch }, data: LoginData) {
      const uuid = await authService.login(data);
      if (uuid !== '') {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Success,
          text: 'Login successfull',
        });
        commit(StoreMutations.SetLoggedIn, uuid !== '');
      } else {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Warning,
          text: 'Login failed',
        });
      }
    },
    async [StoreActions.Logout]({ commit }) {
      await authService.logout();
      commit(StoreMutations.SetLoggedIn, false);
    },
    async [StoreActions.Auth]({ commit }) {
      const uuid = await authService.update();
      commit(StoreMutations.SetLoggedIn, uuid !== '');
    },
    async [StoreActions.GetFamily]({ state, commit }, key: string) {
      const family = state.families.find((f) => `${f.key}-${f.uuid}` === key);
      commit(StoreMutations.SetFamily, family || null);
    },
    async [StoreActions.SaveFamily]({ dispatch, commit }, family: Family) {
      dispatch(StoreActions.Notify, {
        type: NotificationType.Info,
        text: 'Saving...',
      });
      const families = await familyService.save(family);

      if (families.length) {
        commit(StoreMutations.SetFamilies, families);
        dispatch(StoreActions.Notify, {
          type: NotificationType.Success,
          text: 'Saved successfully',
        });
      } else {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Error,
          text: 'Failed to save',
        });
      }
    },
    async [StoreActions.DeleteFamily]({ dispatch, commit }, family: Family) {
      dispatch(StoreActions.Notify, {
        type: NotificationType.Info,
        text: 'Deleting...',
      });
      const { families, status } = await familyService.delete(family);

      if (status) {
        commit(StoreMutations.SetFamilies, families);
        dispatch(StoreActions.Notify, {
          type: NotificationType.Success,
          text: 'Deleted successfully',
        });
      } else {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Error,
          text: 'Failed to delete',
        });
      }
    },
    async [StoreActions.PruneMembers]({ dispatch }, year: number) {
      dispatch(StoreActions.Notify, {
        type: NotificationType.Info,
        text: 'Deleting...',
      });
      const { status } = await familyService.deleteMembers(year);

      if (status) {
        dispatch(StoreActions.GetMembers);
        dispatch(StoreActions.Notify, {
          type: NotificationType.Success,
          text: 'Deleted successfully',
        });
      } else {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Error,
          text: 'Failed to delete',
        });
      }
    },
    async [StoreActions.ApproveSignup]({ dispatch, commit }, family: Family) {
      dispatch(StoreActions.Notify, {
        type: NotificationType.Info,
        text: 'Approving...',
      });
      const families = await familyService.save({ ...family, pending: false });

      if (families.length) {
        commit(StoreMutations.SetFamilies, families);
        dispatch(StoreActions.Notify, {
          type: NotificationType.Success,
          text: 'Approved successfully',
        });
      } else {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Error,
          text: 'Failed to approve',
        });
      }
    },
    async [StoreActions.GetFamilies]({ commit }) {
      const families = await familyService.getFamilies();
      commit(
        StoreMutations.SetFamilies,
        families.sort((a, b) => (a.name < b.name ? -1 : 1)),
      );
    },
    async [StoreActions.GetReporting]({ commit }) {
      const reporting = await familyService.getReporting();
      commit(StoreMutations.SetReporting, reporting);
    },
    async [StoreActions.GetMembers]({ state, dispatch, commit }) {
      await dispatch(StoreActions.GetFamilies);
      commit(
        StoreMutations.SetMembers,
        state.families
          .reduce((acc: Member[], fam: Family) => {
            return [
              ...acc,
              ...fam.members.map(
                (m: FamilyMember, index: number): Member => {
                  return {
                    ...m,
                    key: fam.key,
                    uuid: fam.uuid,
                    index: index,
                    street: fam.street,
                    number: fam.number,
                    code: fam.code,
                    neighbourhood: fam.neighbourhood,
                    place: fam.place,
                    contacts: fam.contacts,
                    pending: fam.pending,
                  };
                },
              ),
            ];
          }, [])
          .sort((a, b) =>
            a.last === b.last
              ? a.first < b.first
                ? -1
                : 1
              : a.last < b.last
              ? -1
              : 1,
          ),
      );
    },
    async [StoreActions.Upload]({ dispatch }, data: FormData) {
      dispatch(StoreActions.Notify, {
        type: NotificationType.Info,
        text: 'Uploading...',
      });
      const success = await uploadService.upload(data);

      if (success) {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Success,
          text: success,
        });
      } else {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Error,
          text: 'Failed to upload data',
        });
      }
    },
    async [StoreActions.SaveRegistration]({ dispatch }, family: Family) {
      dispatch(StoreActions.Notify, {
        type: NotificationType.Info,
        text: 'Versturen...',
      });
      const res = await familyService.register(family);

      if (res) {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Success,
          text: 'De registratie is verstuurd',
        });
      } else {
        dispatch(StoreActions.Notify, {
          type: NotificationType.Error,
          text: 'De registratie kon niet worden verstuurd',
        });
      }
    },
    [StoreActions.Notify]({ commit, dispatch }, notification: Notification) {
      const uuid = UUIDUtil.uuid4();
      commit(StoreMutations.Notify, { ...notification, uuid });
      const delays: { [index: string]: number } = {
        [NotificationType.Info]: 2000,
        [NotificationType.Success]: 2500,
        [NotificationType.Warning]: 6000,
        [NotificationType.Error]: 10000000,
      };
      dispatch(StoreMutations.Dismiss, {
        uuid,
        text: notification.text,
        delay: delays[notification.type],
      });
    },
    [StoreActions.Dismiss]({ commit }, data) {
      setTimeout(() => {
        commit(StoreMutations.Dismiss, data.uuid);
      }, data.delay);
    },
  },
  getters: {
    years: (state: DoorbraakStore): FieldSelectItem[] => {
      return state.members
        .reduce((acc: number[], member: Member) => {
          const year = new Date(member.birth).getFullYear();
          return acc.includes(year) ? acc : [...acc, year].sort();
        }, [])
        .map((value) => ({
          label: `${value}`,
          value,
        }));
    },
  },
});
